import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { camOn, micOn, type CaptureKind, type SegmentMode, type SourceFeed, type StreamSource } from '@rabbitfuel/shared';
import { useRealtime } from './realtime.tsx';
import { openCamera, openMic, openScreen, Publisher, stopStream, Subscriber, type PeerState } from './webrtc.ts';
import { AudioMix } from './audioMix.ts';
import { useIngestStreams } from './whep.ts';
import { BackgroundEffect, preloadImage, type SegmentStats, type SegmentTarget } from './segment.ts';

/**
 * Sendet den lokalen Stream an alle Zuschauer, die *diese* Quelle abonniert
 * haben. Gibt zurueck, wie viele Gegenstellen gerade dranhaengen.
 *
 * Die `sourceId` ist keine Zierde: ein Tab kann zwei Quellen senden (Kamera
 * und geteilter Bildschirm), und dann laufen zwei Publisher im selben
 * Nachrichtenstrom. Ohne die Zuordnung wuerde jeder von beiden auf jedes
 * `peer:subscribe` antworten -- wer die Kamera abonniert, bekaeme womoeglich
 * den Bildschirm. Ohne angemeldete Quelle wird bewusst niemand bedient.
 */
/**
 * `maxVideoHeight` begrenzt, was an die Zuschauer geht -- siehe
 * `Publisher.limitVideo`. Ohne Angabe geht das Bild in voller Aufloesung raus.
 */
export function usePublisher(
  stream: MediaStream | null,
  sourceId: string | null | undefined,
  maxVideoHeight?: number,
): number {
  const { state, send, on } = useRealtime();
  const iceKey = JSON.stringify(state.iceServers);
  const publisherRef = useRef<Publisher | null>(null);
  const [generation, setGeneration] = useState(0);
  const [subscribers, setSubscribers] = useState<string[]>([]);
  /* Als Ref, damit eine neu vergebene sourceId nicht den Handler neu haengt. */
  const mineRef = useRef(sourceId);
  mineRef.current = sourceId;

  useEffect(() => {
    // Ohne angemeldete Quelle gibt es nichts zu senden -- und keine Adresse,
    // unter der ein Zuschauer uns abonnieren koennte.
    if (!sourceId) return;
    const publisher = new Publisher(
      (to, data) => send({ t: 'rtc:signal', to, data }),
      state.iceServers,
      sourceId,
    );
    publisherRef.current = publisher;
    publisher.limitVideo(maxVideoHeight ?? null);
    setGeneration((g) => g + 1);
    return () => {
      publisher.dispose();
      publisherRef.current = null;
      setSubscribers([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iceKey, send, sourceId, maxVideoHeight]);

  useEffect(
    () =>
      on((msg) => {
        if (msg.t === 'peer:subscribe') {
          if (!mineRef.current || msg.sourceId !== mineRef.current) return;
          setSubscribers((prev) => (prev.includes(msg.subscriberId) ? prev : [...prev, msg.subscriberId]));
        } else if (msg.t === 'peer:unsubscribe') {
          if (!mineRef.current || msg.sourceId !== mineRef.current) return;
          publisherRef.current?.removeSubscriber(msg.subscriberId);
          setSubscribers((prev) => prev.filter((id) => id !== msg.subscriberId));
        } else if (msg.t === 'rtc:signal') {
          publisherRef.current?.handleSignal(msg.from, msg.data);
        }
      }),
    [on],
  );

  useEffect(() => {
    publisherRef.current?.setStream(stream);
  }, [stream, generation]);

  useEffect(() => {
    if (!stream) return;
    for (const id of subscribers) void publisherRef.current?.addSubscriber(id);
  }, [subscribers, stream, generation]);

  return subscribers.length;
}

export interface Subscriptions {
  streams: Record<string, MediaStream>;
  /** Verbindungszustand je Quelle -- damit die Regie sieht, woran es haengt. */
  states: Record<string, PeerState>;
}

/** Ein laufendes Abo, so wie es beim Server liegt. */
interface ActiveSubscription {
  /** Verbindung des Senders -- die Adresse fuer die Signalisierung. */
  publisher: string;
  /** Unsere eigene Verbindung, ueber die das Abo beim Server angemeldet ist. */
  via: string;
  /** Neuversuche seit dem letzten Moment mit Bild -- fuer den Abstand. */
  retries: number;
}

/**
 * Wie lange eine Verbindung in einem Zustand haengen darf, bevor die Wache
 * sie neu abonniert. `disconnected` heilt sich oft von selbst (kurzer
 * WLAN-Aussetzer) und bekommt deshalb ein paar Sekunden; `failed` und
 * `closed` heilen nie. Kommt ein Aufbau gar nicht erst zustande -- das
 * Angebot ging verloren --, steht er in `new` oder `connecting`.
 */
const STALL_MS: Partial<Record<PeerState, number>> = {
  new: 12_000,
  connecting: 12_000,
  disconnected: 5_000,
  failed: 1_000,
  closed: 1_000,
};
/** Hoechstens so lange liegt zwischen zwei Neuversuchen derselben Quelle. */
const MAX_RETRY_GAP_MS = 20_000;

/**
 * Abonniert genau die uebergebenen Quellen und gibt die eingehenden
 * MediaStreams pro sourceId zurueck. Wegfallende Quellen werden abgemeldet.
 *
 * Und es haelt sie am Laufen, denn zwei Dinge heilen sich nicht von selbst:
 *
 * - **Die eigene WebSocket verbindet neu.** Der Server hat die Abos der alten
 *   Verbindung dann schon abgemeldet und die Sender haben ihre Verbindungen zu
 *   uns geschlossen. Ohne neues Abo bliebe das Bild fuer immer stehen.
 * - **Eine PeerConnection scheitert** (Funkloch, Netzwechsel) oder kommt nie
 *   zustande. Der Server weiss davon nichts -- er vermittelt nur. Deshalb
 *   meldet die Wache das Abo ab und wieder an: der Sender schickt ein neues
 *   Angebot, und das letzte Bild bleibt bis dahin stehen.
 */
export function useSubscriptions(all: StreamSource[]): Subscriptions {
  const { state, send, on } = useRealtime();
  // RTMP-Eingaenge haben keinen Publisher im Raum -- ihr Bild kommt per WHEP (whep.ts).
  const wanted = all.filter((s) => !s.ingest);
  const ingest = useIngestStreams(
    all.filter((s) => s.ingest),
    state.event?.slug,
    state.iceServers,
  );
  const iceKey = JSON.stringify(state.iceServers);
  const myConnId = state.self?.connectionId ?? null;
  const subscriberRef = useRef<Subscriber | null>(null);
  /** sourceId -> Abo, wie es gerade beim Server liegt. */
  const activeRef = useRef<Map<string, ActiveSubscription>>(new Map());
  /** sourceId -> Verbindungszustand und seit wann -- fuer die Wache. */
  const peerRef = useRef<Map<string, { state: PeerState; since: number }>>(new Map());
  /* Als Refs, damit die Wache immer den neuesten Stand liest. */
  const wantedRef = useRef<Map<string, StreamSource>>(new Map());
  wantedRef.current = new Map(wanted.map((s) => [s.id, s]));
  const openRef = useRef(false);
  openRef.current = state.status === 'open';
  const [generation, setGeneration] = useState(0);
  const [streams, setStreams] = useState<Record<string, MediaStream>>({});
  const [states, setStates] = useState<Record<string, PeerState>>({});

  useEffect(() => {
    const subscriber = new Subscriber(
      (to, data) => send({ t: 'rtc:signal', to, data }),
      state.iceServers,
      (sourceId, stream) => setStreams((prev) => ({ ...prev, [sourceId]: stream })),
      (sourceId) =>
        setStreams((prev) => {
          if (!(sourceId in prev)) return prev;
          const next = { ...prev };
          delete next[sourceId];
          return next;
        }),
      (sourceId, peerState) => {
        peerRef.current.set(sourceId, { state: peerState, since: Date.now() });
        setStates((prev) => ({ ...prev, [sourceId]: peerState }));
      },
    );
    subscriberRef.current = subscriber;
    activeRef.current = new Map();
    peerRef.current = new Map();
    setGeneration((g) => g + 1);
    return () => {
      // Der Server fuehrt die Abos pro Verbindung -- ohne Abmeldung wuerde ein
      // erneutes subscribe fuer dieselbe Quelle ignoriert.
      for (const sourceId of activeRef.current.keys()) send({ t: 'unsubscribe', sourceId });
      subscriber.closeAll();
      subscriberRef.current = null;
      activeRef.current = new Map();
      peerRef.current = new Map();
      setStreams({});
      setStates({});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iceKey, send]);

  useEffect(
    () =>
      on((msg) => {
        if (msg.t === 'rtc:signal') subscriberRef.current?.handleSignal(msg.from, msg.data);
      }),
    [on],
  );

  /*
   * Ab- und wieder anmelden, das Bild bleibt dabei stehen. Die Abmeldung ist
   * noetig, auch wenn das Abo beim Server vielleicht gar nicht mehr liegt:
   * ein subscribe fuer eine Quelle, die der Server schon fuehrt, ignoriert er
   * -- und dann kaeme kein neues Angebot.
   */
  const resubscribe = useCallback(
    (id: string, publisher: string, via: string, retries: number) => {
      const subscriber = subscriberRef.current;
      if (!subscriber) return;
      subscriber.rebind(id, publisher);
      send({ t: 'unsubscribe', sourceId: id });
      send({ t: 'subscribe', sourceId: id });
      activeRef.current.set(id, { publisher, via, retries });
      peerRef.current.set(id, { state: 'new', since: Date.now() });
    },
    [send],
  );

  const wantedKey = wanted
    .map((s) => `${s.id}@${s.connectionId}`)
    .sort()
    .join('|');

  useEffect(() => {
    const subscriber = subscriberRef.current;
    // Ohne eigene Verbindung gibt es keinen Server, der das Abo fuehren koennte.
    if (!subscriber || !myConnId) return;
    const want = new Map(wanted.map((s) => [s.id, s]));

    for (const [id, active] of [...activeRef.current]) {
      const next = want.get(id);
      if (next && next.connectionId === active.publisher && active.via === myConnId) continue;
      if (next) {
        /*
         * Der Sender kommt aus einer neuen Verbindung (sein Reload), oder
         * wir tun es (unser Reconnect). Beides braucht ein frisches Abo; das
         * Bild bleibt dabei stehen -- `rebind` gibt den Stream nicht zurueck.
         */
        resubscribe(id, next.connectionId, myConnId, 0);
        continue;
      }
      // Die Quelle ist weg: `forget` gibt den Stream zurueck.
      subscriber.forget(id);
      send({ t: 'unsubscribe', sourceId: id });
      activeRef.current.delete(id);
      peerRef.current.delete(id);
    }
    for (const [id, source] of want) {
      if (activeRef.current.has(id)) continue;
      subscriber.expect(id, source.connectionId);
      send({ t: 'subscribe', sourceId: id });
      activeRef.current.set(id, { publisher: source.connectionId, via: myConnId, retries: 0 });
      peerRef.current.set(id, { state: 'new', since: Date.now() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantedKey, generation, send, myConnId, resubscribe]);

  /*
   * Die Wache. Eine Verbindung, die in einem schlechten Zustand haengt, wird
   * neu abonniert -- mit wachsendem Abstand, damit ein Sender, der gerade gar
   * nicht erreichbar ist, nicht im Sekundentakt Angebote baut.
   */
  useEffect(() => {
    const timer = window.setInterval(() => {
      // Ohne Verbindung zum Server laeuft keine Signalisierung; nach dem
      // Wiederverbinden meldet der Effekt oben ohnehin alles neu an.
      if (!openRef.current) return;
      const now = Date.now();
      for (const [id, active] of activeRef.current) {
        const source = wantedRef.current.get(id);
        // Der Sender ist selbst weg -- er meldet sich mit neuer Verbindung zurueck.
        if (!source || source.reconnecting) continue;
        const peer = peerRef.current.get(id) ?? { state: 'new' as PeerState, since: now };
        if (peer.state === 'connected') {
          active.retries = 0;
          continue;
        }
        const limit = STALL_MS[peer.state];
        if (limit === undefined) continue;
        const gap = Math.min(MAX_RETRY_GAP_MS, limit * 2 ** active.retries);
        if (now - peer.since < gap) continue;
        console.warn(`[rtc] ${source.label}: Verbindung ${peer.state} -- baue neu auf`);
        resubscribe(id, active.publisher, active.via, active.retries + 1);
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resubscribe]);

  return useMemo(
    () => ({ streams: { ...streams, ...ingest.streams }, states: { ...states, ...ingest.states } }),
    [streams, states, ingest.streams, ingest.states],
  );
}

/* ------------------------------------------------ Hintergrund der Kamera */

/**
 * Was hinter die Person soll, wenn der Hintergrund ersetzt wird. Kommt aus
 * dem Buehnen-Hintergrund des Events, damit das gesendete Bild und der
 * Player-Hintergrund dasselbe Motiv zeigen.
 */
export interface EffectTarget {
  url: string | null;
  color: string;
  blur: number;
  dim: number;
}

export interface CameraEffects {
  mode: SegmentMode;
  setMode: (mode: SegmentMode) => void;
  /** Der aufbereitete Stream, oder null wenn die rohe Kamera gesendet wird. */
  stream: MediaStream | null;
  busy: boolean;
  error: string | null;
  stats: SegmentStats | null;
  /** Freistellen gibt es nur fuer eine Kamera, nicht fuer einen Bildschirm. */
  available: boolean;
}

/** Bildrate der aufbereiteten Spur. 24 reicht fuer Sport und spart Rechenzeit. */
const EFFECT_FPS = 24;
const SEGMENT_KEY = 'rabbitfuel:segment';

const readMode = (): SegmentMode => {
  try {
    const raw = sessionStorage.getItem(SEGMENT_KEY);
    return raw === 'blur' || raw === 'backdrop' ? raw : 'off';
  } catch {
    return 'off';
  }
};

/**
 * Freistellen der eigenen Kamera.
 *
 * Bewusst *neben* `useCapture` und nicht darin: die Aufnahme bleibt damit
 * unberuehrt und ohne Effekt genau so schnell wie vorher. Die Regie sendet
 * `effects.stream ?? capture.stream` -- ein Ausfall der Segmentierung kostet
 * also den Effekt, nicht den Stream.
 *
 * Der gewaehlte Modus liegt im `sessionStorage` und ueberlebt damit einen
 * Reload, so wie die Aufnahme selbst.
 */
export function useCameraEffects(
  source: MediaStream | null,
  kind: CaptureKind | null,
  target: EffectTarget,
): CameraEffects {
  const [mode, setMode] = useState<SegmentMode>(readMode);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<SegmentStats | null>(null);

  /*
   * Modus, Hintergrundbild und Regler werden pro Bild *gelesen*, nicht als
   * Abhaengigkeit gefuehrt. Sonst baute jeder Zug an einem Schieberegler die
   * ganze Kette neu auf -- Modell laden inklusive.
   */
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const targetRef = useRef(target);
  targetRef.current = target;
  const imageRef = useRef<HTMLImageElement | null>(null);

  const available = kind === 'camera';
  const enabled = available && mode !== 'off' && Boolean(source);

  useEffect(() => {
    try {
      sessionStorage.setItem(SEGMENT_KEY, mode);
    } catch {
      /* Privater Modus -- dann gilt die Wahl nur fuer diese Seite. */
    }
  }, [mode]);

  /* Hintergrundbild vorladen, sobald es sich aendert. */
  useEffect(() => {
    let dropped = false;
    void preloadImage(target.url).then((image) => {
      if (!dropped) imageRef.current = image;
    });
    return () => {
      dropped = true;
    };
  }, [target.url]);

  useEffect(() => {
    if (!enabled || !source) {
      setStream(null);
      setStats(null);
      setError(null);
      return;
    }

    let effect: BackgroundEffect | null = null;
    let output: MediaStream | null = null;
    let disposed = false;
    let poll: number | undefined;
    setBusy(true);
    setError(null);

    const run = async () => {
      const built = await BackgroundEffect.create(source);
      if (disposed) return built.stop();
      effect = built;

      built.start(() => {
        const current = targetRef.current;
        const value: SegmentTarget = {
          image: imageRef.current,
          color: current.color,
          blur: current.blur,
          dim: current.dim,
        };
        return { mode: modeRef.current === 'blur' ? 'blur' : 'backdrop', target: value };
      });

      // Bild aus dem Canvas, Ton unveraendert von der Kamera.
      const painted = built.captureStream(EFFECT_FPS);
      output = new MediaStream([...painted.getVideoTracks(), ...source.getAudioTracks()]);
      setStream(output);
      setStats(built.getStats());
      poll = window.setInterval(() => setStats(built.getStats()), 1000);
    };

    void run()
      .catch((err: unknown) => {
        if (disposed) return;
        setError(err instanceof Error ? err.message : 'Freistellen ist fehlgeschlagen.');
        // Ohne Effekt geht die rohe Kamera raus -- besser als kein Bild.
        setStream(null);
      })
      .finally(() => !disposed && setBusy(false));

    return () => {
      disposed = true;
      window.clearInterval(poll);
      effect?.stop();
      // Nur die selbst erzeugte Spur beenden; die Kamera gehoert `useCapture`.
      output?.getVideoTracks().forEach((t) => t.stop());
      setStream(null);
      setStats(null);
    };
  }, [enabled, source]);

  return { mode, setMode, stream, busy, error, stats, available };
}

/**
 * Die Spuren einer Aufnahme, einzeln adressierbar -- denn genau so schaltet
 * die Regie sie: Mikrofon, Bildschirmton und Bild unabhaengig voneinander.
 */
export interface CaptureTracks {
  video: MediaStreamTrack | null;
  mic: MediaStreamTrack | null;
  /** Ton des geteilten Bildschirms. Bei einer Kamera immer null. */
  system: MediaStreamTrack | null;
}

const noTracks: CaptureTracks = { video: null, mic: null, system: null };

/** Spuren eines gewoehnlichen Kamerastreams -- Bild plus Mikrofon. */
export const cameraTracks = (stream: MediaStream | null): CaptureTracks =>
  stream
    ? { video: stream.getVideoTracks()[0] ?? null, mic: stream.getAudioTracks()[0] ?? null, system: null }
    : noTracks;

/**
 * Schaltet die Spuren dieses Geraets auf den Zustand, den der Server fuehrt.
 *
 * Hier wird „Mikro aus" in der Regie zu echter Stille: eine abgeschaltete
 * Spur liefert keine Daten mehr, ist also bei Zuschauern, im RTMP-Ausgang
 * und in jeder Aufnahme gleichzeitig weg. Das ist der Unterschied zu einem
 * stumm gestellten Player -- der betrifft nur den, der ihn stumm gestellt hat.
 *
 * Der Zustand kommt aus der Quellenliste vom Server und nicht aus dem
 * lokalen React-Zustand. Deshalb steht er nach einem Reload wieder richtig,
 * und deshalb wirkt der Knopf der Regie auch auf einem fremden Handy.
 */
export function useFeedGate(tracks: CaptureTracks, feed: SourceFeed | null | undefined) {
  // Ohne angemeldete Quelle gibt es nichts abzuschalten -- dann geht alles raus.
  // Mit ihr zaehlen Regie und Sender gemeinsam: aus ist, was einer ausschaltet.
  const mic = feed ? micOn(feed) : true;
  const system = feed?.system ?? true;
  const cam = feed ? camOn(feed) : true;

  useEffect(() => {
    if (tracks.mic) tracks.mic.enabled = mic;
    if (tracks.system) tracks.system.enabled = system;
    if (tracks.video) tracks.video.enabled = cam;
  }, [tracks, mic, system, cam]);
}

/** Welche Geraete eine Aufnahme benutzen soll. Leer = der Browser entscheidet. */
export interface CaptureChoice {
  /** Kamera-Geraet. Nur fuer `kind: 'camera'`. */
  camera?: string;
  /** Mikrofon-Geraet. Gilt fuer die Kamera *und* fuer den geteilten Bildschirm. */
  mic?: string;
  /** Vorder- oder Rueckkamera -- greift nur ohne gewaehltes Geraet. */
  facing?: 'user' | 'environment';
}

export interface Capture {
  kind: CaptureKind;
  stream: MediaStream | null;
  /** Die Spuren dahinter -- fuer `useFeedGate` und die Anzeige im Pult. */
  tracks: CaptureTracks;
  /**
   * Warum bei der Bildschirmuebertragung kein Mikrofon dabei ist. Kein
   * Fehler der Aufnahme: Bild und Bildschirmton laufen trotzdem.
   */
  micError: string | null;
  error: string | null;
  busy: boolean;
  /** Welche Geraete gerade laufen -- die Auswahlmenues zeigen das an. */
  choice: CaptureChoice;
  /**
   * Diese Aufnahme lief vor dem Reload und konnte nicht von selbst wieder
   * anlaufen -- bei einem geteilten Bildschirm immer, weil
   * `getDisplayMedia` zwingend einen Klick braucht. Die Oberflaeche macht
   * daraus ein Angebot statt stillem Nichts.
   */
  resumable: boolean;
  /**
   * Die Wiederaufnahme nach einem Reload laeuft noch. Wichtig fuer alles, was
   * aus "kein Stream" schliesst, die Aufnahme sei abgeschaltet worden -- in
   * diesen ersten Sekunden waere dieser Schluss falsch.
   */
  resuming: boolean;
  /**
   * Startet die Aufnahme -- oder wechselt im Betrieb das Geraet. Der zweite
   * Fall ist keine Sonderbehandlung wert: die Quelle bleibt angemeldet, der
   * Publisher zieht die laufenden Verbindungen per `replaceTrack` auf die
   * neuen Spuren um. Fuer die Zuschauer wechselt nur das Bild.
   */
  start: (choice?: CaptureChoice) => Promise<MediaStream | null>;
  stop: () => void;
  /** Wiederaufnahme verwerfen, ohne sie zu starten. */
  dismissResume: () => void;
}

/** Was dieser Tab zuletzt aufgenommen hat -- ueberlebt einen Reload. */
const resumeKey = (kind: CaptureKind) => `rabbitfuel:capture:${kind}`;

const readResume = (kind: CaptureKind): CaptureChoice | null => {
  try {
    const raw = sessionStorage.getItem(resumeKey(kind));
    return raw ? (JSON.parse(raw) as CaptureChoice) : null;
  } catch {
    return null;
  }
};

const writeResume = (kind: CaptureKind, choice: CaptureChoice | null) => {
  try {
    if (choice) sessionStorage.setItem(resumeKey(kind), JSON.stringify(choice));
    else sessionStorage.removeItem(resumeKey(kind));
  } catch {
    /* Privater Modus -- dann gibt es nach dem Reload eben keinen Vorschlag. */
  }
};

/**
 * Eine laufende Aufnahme mit allem, was zu ihr gehoert.
 *
 * `out` ist das, was gesendet wird -- Bild plus *eine* Tonspur. `raw` sind
 * die Geraetestreams dahinter, und nur die duerfen gestoppt werden: ein
 * `stop()` auf der gemischten Spur wuerde den Mischer abwuergen, ohne Kamera
 * oder Mikrofon freizugeben.
 */
interface Bundle {
  kind: CaptureKind;
  out: MediaStream;
  tracks: CaptureTracks;
  raw: MediaStream[];
  mix: AudioMix | null;
}

const closeBundle = (bundle: Bundle | null) => {
  if (!bundle) return;
  bundle.mix?.close();
  for (const stream of bundle.raw) stopStream(stream);
};

/**
 * Oeffnet Kamera oder Bildschirm und macht daraus einen sendefertigen Stream.
 *
 * Der Unterschied zwischen beiden steckt im Ton. Eine Kamera bringt ihr
 * Mikrofon mit -- fertig. Eine Bildschirmfreigabe nicht: `getDisplayMedia`
 * gibt bestenfalls den Ton des geteilten Inhalts heraus, und wer eine stille
 * Folie zeigt und dazu spricht, ginge damit lautlos raus. Deshalb kommt beim
 * Bildschirm ein zweiter Eingang dazu und beide laufen durch den Mischer:
 * ein Bild, ein Ton, zwei Quellen.
 *
 * Die Reihenfolge ist dabei nicht beliebig. Der Bildschirmdialog muss zuerst
 * kommen, weil er die frische Nutzergeste des Klicks braucht -- ein `await`
 * davor, und der Browser lehnt ihn ab. Das Mikrofon fragt danach ohne Geste.
 */
async function openBundle(
  kind: CaptureKind,
  choice: CaptureChoice,
): Promise<{ bundle: Bundle; micError: string | null }> {
  if (kind === 'camera') {
    const cam = await openCamera(choice.facing ?? 'environment', { camera: choice.camera, mic: choice.mic });
    return { bundle: { kind, out: cam, tracks: cameraTracks(cam), raw: [cam], mix: null }, micError: null };
  }

  const screen = await openScreen();
  const video = screen.getVideoTracks()[0] ?? null;
  const system = screen.getAudioTracks()[0] ?? null;

  let micStream: MediaStream | null = null;
  let micError: string | null = null;
  try {
    micStream = await openMic(choice.mic);
  } catch (err) {
    // Kein Grund, die Uebertragung abzubrechen: Bild und Bildschirmton
    // laufen weiter. Die Regie soll nur wissen, warum niemand sie hoert.
    const denied = err instanceof Error && /Permission denied|NotAllowed/i.test(err.message);
    micError = denied
      ? 'Kein Mikrofon freigegeben — es geht nur der Bildschirmton raus.'
      : 'Das Mikrofon ließ sich nicht öffnen — es geht nur der Bildschirmton raus.';
  }
  const mic = micStream?.getAudioTracks()[0] ?? null;

  /*
   * Gemischt wird nur, wenn es wirklich zwei Tonquellen gibt. Eine einzelne
   * geht direkt mit -- das spart einen AudioContext und damit die Stelle, an
   * der am meisten schiefgehen kann. Faellt der Mischer aus, gewinnt das
   * Mikrofon: der Kommentar ist wichtiger als der Hintergrundton.
   */
  const mix = mic && system ? AudioMix.create() : null;
  if (mix) {
    mix.set('system', screen);
    mix.set('mic', micStream);
  }
  const audio = mix?.track ?? mic ?? system;

  const out = new MediaStream([...(video ? [video] : []), ...(audio ? [audio] : [])]);
  return {
    bundle: {
      kind,
      out,
      tracks: { video, mic, system },
      raw: micStream ? [screen, micStream] : [screen],
      mix,
    },
    micError,
  };
}

/**
 * Eine Aufnahme *einer* Art: Kamera oder geteilter Bildschirm.
 *
 * Bewusst eine Instanz pro Art und nicht ein Hook, der zwischen beiden
 * umschaltet. Die Regie sendet beides gleichzeitig -- Bildschirm als
 * Hauptbild, Kamera im kleinen Fenster --, und zwei unabhaengige Aufnahmen
 * sind genau das, was der Server als zwei Quellen fuehrt. Ein Hook mit
 * Umschalter muesste denselben Zustand doppelt halten und beim Wechsel
 * jedes Mal eine Quelle abmelden.
 *
 * Nach einem Reload laeuft eine Kamera von selbst wieder an: die Erlaubnis
 * gilt im Secure Context weiter, es braucht also keinen zweiten Dialog. Eine
 * Bildschirmfreigabe kann das nicht -- `getDisplayMedia` verlangt in jedem
 * Browser eine frische Nutzergeste. Dafuer gibt es `resumable`.
 */
export function useCapture(kind: CaptureKind): Capture {
  const [bundle, setBundle] = useState<Bundle | null>(null);
  const [choice, setChoice] = useState<CaptureChoice>(() => readResume(kind) ?? {});
  const [micError, setMicError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resumable, setResumable] = useState(false);
  // Beim ersten Rendern schon bekannt: liegt ein Hinweis vor, wird gleich
  // wiederaufgenommen. Sonst gaebe es ein Fenster, in dem die Seite denkt,
  // hier sei nie eine Aufnahme gelaufen.
  const [resuming, setResuming] = useState(() => kind === 'camera' && readResume(kind) !== null);
  const bundleRef = useRef<Bundle | null>(null);
  /** Die Aufnahme, die `start` gerade ersetzt -- ihr Ende ist kein Abbruch. */
  const replacingRef = useRef<Bundle | null>(null);

  const apply = useCallback((next: Bundle | null) => {
    if (bundleRef.current && bundleRef.current !== next) closeBundle(bundleRef.current);
    bundleRef.current = next;
    setBundle(next);
    if (!next) setMicError(null);
  }, []);

  const start = useCallback(
    async (next: CaptureChoice = {}) => {
      setBusy(true);
      setError(null);
      /*
       * Ein Geraetewechsel oeffnet das neue Geraet, solange das alte noch
       * laeuft -- am Rechner ein nahtloser Wechsel. iOS laesst aber nur eine
       * Kamera zur Zeit zu und beendet die alte von selbst, sobald die neue
       * aufgeht. Dieses `ended` darf die Aufnahme nicht abmelden, sonst
       * reisst es die neue Kamera gleich mit.
       */
      const previous = bundleRef.current;
      replacingRef.current = previous;
      try {
        const opened = await openBundle(kind, next).catch((err: unknown) => {
          // Android sagt es offen: zwei Kameras zugleich gehen nicht. Dann
          // die alte zuerst freigeben -- kostet einen Moment Bild.
          if (kind !== 'camera' || !previous || !(err instanceof Error && err.name === 'NotReadableError')) throw err;
          closeBundle(previous);
          return openBundle(kind, next);
        });
        const own = opened.bundle;
        // Beendet der Nutzer das Teilen ueber die Browserleiste, hier mitbekommen.
        own.tracks.video?.addEventListener('ended', () => {
          if (bundleRef.current !== own || replacingRef.current === own) return;
          apply(null);
          writeResume(kind, null);
        });
        apply(own);
        setMicError(opened.micError);
        setChoice(next);
        writeResume(kind, next);
        setResumable(false);
        return own.out;
      } catch (err) {
        // Ist die alte Aufnahme beim Versuch draufgegangen, nicht so tun, als
        // liefe sie noch.
        if (previous && bundleRef.current === previous && previous.tracks.video?.readyState === 'ended') {
          apply(null);
          writeResume(kind, null);
        }
        const message = err instanceof Error ? err.message : 'Aufnahme fehlgeschlagen.';
        setError(
          message.includes('Permission denied')
            ? 'Zugriff abgelehnt — bitte im Browser erlauben.'
            : /OverconstrainedError|Requested device not found/i.test(message)
              ? 'Das gewählte Gerät ist nicht mehr da — bitte ein anderes wählen.'
              : message,
        );
        return null;
      } finally {
        if (replacingRef.current === previous) replacingRef.current = null;
        setBusy(false);
      }
    },
    [apply, kind],
  );

  /* Nach einem Reload dort weitermachen, wo der Tab aufgehoert hat. */
  const resumedRef = useRef(false);
  useEffect(() => {
    if (resumedRef.current) return;
    resumedRef.current = true;
    const hint = readResume(kind);
    if (!hint) return;
    if (kind !== 'camera') {
      // Bildschirm: geht nur mit Klick, also ab hier ein Angebot statt Warten.
      setResumable(true);
      return;
    }
    void start(hint).finally(() => setResuming(false));
  }, [kind, start]);

  useEffect(() => () => closeBundle(bundleRef.current), []);

  return {
    kind,
    stream: bundle?.out ?? null,
    tracks: bundle?.tracks ?? noTracks,
    micError,
    error,
    busy,
    choice,
    resumable,
    resuming,
    start,
    stop: () => {
      apply(null);
      writeResume(kind, null);
      setResumable(false);
    },
    dismissResume: () => {
      writeResume(kind, null);
      setResumable(false);
    },
  };
}

/* ------------------------------------------------------------ Geraete */

export interface DeviceOption {
  id: string;
  label: string;
}

export interface DeviceLists {
  cameras: DeviceOption[];
  mics: DeviceOption[];
  speakers: DeviceOption[];
  /**
   * Ob der Browser die Wiedergabe auf ein bestimmtes Geraet legen kann
   * (`setSinkId`). Chrome und Edge koennen es, Safari und Firefox nicht --
   * dort bleibt die Wahl beim Betriebssystem, und das Menue sagt das.
   */
  canPickSpeaker: boolean;
  refresh: () => void;
}

const canPickSpeaker = () =>
  typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;

/**
 * Die Geraete des Rechners, fuer die Auswahlmenues der Regie.
 *
 * `deps` ist der Grund, warum das ein Hook mit Abhaengigkeit ist und keine
 * einmalige Abfrage: *Namen* gibt der Browser erst heraus, wenn eine
 * Erlaubnis vorliegt. Vorher heisst jedes Mikrofon „Mikrofon" und jede
 * Kamera „Kamera 1". Deshalb wird die Liste nach dem Start einer Aufnahme
 * neu gelesen -- dann stehen dort die echten Namen.
 */
export function useDevices(deps: unknown[] = []): DeviceLists {
  const [lists, setLists] = useState<Omit<DeviceLists, 'refresh' | 'canPickSpeaker'>>({
    cameras: [],
    mics: [],
    speakers: [],
  });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let dropped = false;
    const read = async () => {
      if (!navigator.mediaDevices?.enumerateDevices) return;
      const devices = await navigator.mediaDevices.enumerateDevices().catch(() => []);
      if (dropped) return;
      const pick = (kind: MediaDeviceKind, fallback: string) =>
        devices
          .filter((d) => d.kind === kind && d.deviceId)
          .map((d, i) => ({ id: d.deviceId, label: d.label || `${fallback} ${i + 1}` }));
      setLists({
        cameras: pick('videoinput', 'Kamera'),
        mics: pick('audioinput', 'Mikrofon'),
        speakers: pick('audiooutput', 'Ausgabe'),
      });
    };
    void read();
    // Ein eingestecktes Headset soll im Menue auftauchen, ohne Reload.
    navigator.mediaDevices?.addEventListener?.('devicechange', read);
    return () => {
      dropped = true;
      navigator.mediaDevices?.removeEventListener?.('devicechange', read);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, ...deps]);

  return { ...lists, canPickSpeaker: canPickSpeaker(), refresh: () => setTick((t) => t + 1) };
}

/* ----------------------------------------------------------- Abhoeren */

type SinkAudio = HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };

/** Laufende Nummer je Objekt -- fuer Effekt-Schluessel, die an der Identitaet haengen. */
const objectKeys = new WeakMap<object, number>();
let lastObjectKey = 0;
const objectKey = (object: object) => {
  let key = objectKeys.get(object);
  if (key === undefined) {
    key = ++lastObjectKey;
    objectKeys.set(object, key);
  }
  return key;
};

export interface AudioMonitors {
  /**
   * Der Browser hat das Abspielen verweigert -- Ton ohne vorherige Beruehrung
   * der Seite. Beim naechsten Tipp irgendwo laeuft es von selbst an; wer
   * einen Knopf anbieten will, ruft `resume` daraus auf.
   */
  blocked: boolean;
  resume: () => void;
}

/**
 * Spielt Tonspuren ab, eine Wiedergabe je Stream -- in der Regie die Handys
 * im Gespraech, am Handy die Sprechleitung der Regie.
 *
 * Die Audio-Elemente entstehen im Code und nicht im JSX, weil sie nichts
 * anzuzeigen haben: sie sind Lautsprecher, keine Bauteile der Seite. Und sie
 * sind bewusst getrennt von den `<video>`-Elementen der Vorschauen -- die
 * bleiben stumm, sonst haette jede Vorschau ihren eigenen Ton.
 *
 * Ein Element je Stream und kein gemeinsamer Mischer: ein `<audio>` spielt
 * genau eine Tonspur, und Web Audio bekaeme den Ton einer WebRTC-Gegenstelle
 * in Chrome ohnehin nur ueber ein Medienelement.
 */
export function useAudioMonitors(
  streams: MediaStream[],
  { enabled, deviceId }: { enabled: boolean; deviceId?: string },
): AudioMonitors {
  const elementsRef = useRef(new Map<MediaStream, SinkAudio>());
  const streamsRef = useRef(streams);
  streamsRef.current = streams;
  const [blocked, setBlocked] = useState(false);

  const start = useCallback((audio: SinkAudio) => {
    void audio
      .play()
      .then(() => setBlocked(false))
      .catch((err: unknown) => {
        // Nur die Sperre ohne Beruehrung meldet sich so -- alles andere
        // (Stream weg, Element abgeraeumt) ist kein Grund fuer einen Knopf.
        if (err instanceof DOMException && err.name === 'NotAllowedError') setBlocked(true);
      });
  }, []);

  const resume = useCallback(() => {
    for (const audio of elementsRef.current.values()) if (audio.paused) start(audio);
  }, [start]);

  /*
   * Am Stream-*Objekt*, nicht an der Liste (die ist bei jedem Rendern neu)
   * und nicht an `stream.id`: baut sich die Verbindung zum Handy neu auf,
   * kommt ein neuer Stream mit derselben Kennung. Mit der Kennung als
   * Schluessel spielte das Element dann weiter den alten, toten Stream --
   * Stille, obwohl der Ton ankommt.
   */
  const key = enabled ? streams.map(objectKey).join('|') : '';

  useEffect(() => {
    const elements = elementsRef.current;
    const wanted = new Set(enabled ? streamsRef.current : []);
    for (const [stream, audio] of [...elements]) {
      if (wanted.has(stream)) continue;
      audio.pause();
      audio.srcObject = null;
      elements.delete(stream);
    }
    for (const stream of wanted) {
      if (elements.has(stream)) continue;
      const audio: SinkAudio = new Audio();
      audio.srcObject = stream;
      if (deviceId && audio.setSinkId) void audio.setSinkId(deviceId).catch(() => undefined);
      elements.set(stream, audio);
      start(audio);
    }
    if (wanted.size === 0) setBlocked(false);
    // `deviceId` steht unten in einem eigenen Effekt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, start]);

  useEffect(() => {
    if (!deviceId) return;
    for (const audio of elementsRef.current.values()) void audio.setSinkId?.(deviceId).catch(() => undefined);
  }, [deviceId]);

  // Gesperrt: beim naechsten Tipp irgendwo auf der Seite nachholen.
  useEffect(() => {
    if (!blocked) return;
    window.addEventListener('pointerdown', resume, true);
    window.addEventListener('keydown', resume, true);
    return () => {
      window.removeEventListener('pointerdown', resume, true);
      window.removeEventListener('keydown', resume, true);
    };
  }, [blocked, resume]);

  useEffect(
    () => () => {
      for (const audio of elementsRef.current.values()) {
        audio.pause();
        audio.srcObject = null;
      }
      elementsRef.current.clear();
    },
    [],
  );

  return { blocked, resume };
}

/** Haengt einen MediaStream an ein <video> (oder <audio>), ohne bei jedem Render neu zu setzen. */
export function useVideoStream<T extends HTMLMediaElement = HTMLVideoElement>(stream: MediaStream | null | undefined) {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el.srcObject !== (stream ?? null)) el.srcObject = stream ?? null;
    if (stream) void el.play().catch(() => undefined);
  }, [stream]);
  return ref;
}

export const peerStateLabel: Record<PeerState, string> = {
  new: 'wird aufgebaut',
  connecting: 'verbindet',
  connected: 'verbunden',
  disconnected: 'Verbindung wackelt',
  failed: 'fehlgeschlagen',
  closed: 'geschlossen',
};
